import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import io from 'socket.io-client';
import { StarIcon } from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { IconComponents } from '../components/icons';
import { toast } from 'react-toastify';
import PageEditor from '../components/admin/PageEditor';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Composants pour les différentes sections
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const periodOptions = [
    { value: '24h', label: 'Dernières 24h' },
    { value: 'today', label: "Aujourd'hui" },
    { value: '7d', label: '7 derniers jours' },
    { value: '30d', label: '30 derniers jours' }
  ];

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsResponse, salesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/stats?period=${selectedPeriod}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-data?period=${selectedPeriod}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        })
      ]);

      if (!statsResponse.ok || !salesResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const statsData = await statsResponse.json();
      const salesData = await salesResponse.json();

      setStats(statsData);
      setSalesData(salesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setStats(null);
      setSalesData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#22c55e]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-6 text-center">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={() => fetchStats()} 
          className="neon-button mt-4"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!stats || !salesData) {
    return null;
  }

  const chartData = {
    labels: salesData.labels,
    datasets: [
      {
        label: 'Ventes ($)',
        data: salesData.sales,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#22c55e',
        pointBorderColor: '#22c55e',
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#15803d',
        pointHoverBorderColor: '#15803d'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#22c55e',
        bodyColor: '#fff',
        borderColor: '#22c55e',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            if (selectedPeriod === '24h' || selectedPeriod === 'today') {
              return `${item.label} (Paris)`;
            }
            return item.label;
          },
          label: (context) => {
            return `Ventes: $${context.raw.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#fff',
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#fff',
          callback: (value) => `$${value}`
        },
        beginAtZero: true
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Total Sales in Green */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-medium mb-2">Ventes Totales</h3>
        <div className="flex items-baseline">
          <p className="text-4xl font-bold text-[#22c55e]">${stats.periodSales}</p>
          <span className="ml-2 text-gray-400">sur {periodOptions.find(opt => opt.value === selectedPeriod)?.label.toLowerCase()}</span>
        </div>
      </div>

      {/* Période selector */}
      <div className="flex justify-end mb-4">
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="bg-gray-700 text-white border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#22c55e] focus:border-transparent"
        >
          {periodOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h3 className="text-lg font-medium mb-2">Commandes</h3>
          <p className="text-3xl font-bold text-[#22c55e]">{stats.periodOrders || 0}</p>
          <p className="text-sm text-gray-400 mt-1">
            Complétées: {stats.periodCompletedOrders || 0}
          </p>
        </div>
        <div className="glass-card p-6">
          <h3 className="text-lg font-medium mb-2">Reviews</h3>
          <p className="text-3xl font-bold text-[#22c55e]">{stats.totalFeedbacks || 0}</p>
          <p className="text-sm text-gray-400 mt-1">
            Note moyenne: {(Number(stats.periodAverageRating) || 0).toFixed(1)}/5
          </p>
        </div>
      </div>

      {/* Graphique des ventes */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-medium mb-4">Évolution des ventes</h3>
        <div className="h-[400px]">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Statistiques détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h3 className="text-lg font-medium mb-4">Statistiques des commandes</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Commandes complétées</span>
              <span className="text-[#22c55e]">{stats.periodCompletedOrders || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Commandes annulées</span>
              <span className="text-[#22c55e]">{stats.periodCancelledOrders || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Valeur moyenne</span>
              <span className="text-[#22c55e]">${(Number(stats.periodAverageOrderValue) || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
        <div className="glass-card p-6">
          <h3 className="text-lg font-medium mb-4">Statistiques clients</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Nouveaux clients</span>
              <span className="text-[#22c55e]">{stats.periodNewCustomers || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Clients fidèles</span>
              <span className="text-[#22c55e]">{stats.periodReturningCustomers || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Note moyenne</span>
              <span className="text-[#22c55e]">{(Number(stats.periodAverageRating) || 0).toFixed(1)} / 5</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockInput, setStockInput] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    price: '',
    image: '',
    features: [],
    type: '',
    description: '',
    deliveryType: 'serials',
    apiKeyType: '',
    requiresGuildId: false,
    minQuantity: 1,
    maxQuantity: 1000,
    memberType: 'online',
    discount: 0,
    onHold: false,
    customUrl: '',
    showBoostInstructions: false
  });
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedProducts, setReorderedProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchCategories();

    const socket = io(process.env.REACT_APP_API_URL);
    
    socket.on('product_updated', (updatedProduct) => {
      setProducts(prevProducts => 
        prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
      );
    });

    socket.on('product_deleted', (deletedProductId) => {
      setProducts(prevProducts => prevProducts.filter(p => p.id !== deletedProductId));
    });

    socket.on('products_reordered', (newProducts) => {
      setProducts(newProducts);
    });

    socket.on('category_created', (newCategory) => {
      setCategories(prev => [...prev, newCategory]);
    });

    socket.on('category_updated', (updatedCategory) => {
      setCategories(prev => prev.map(cat => 
        cat.id === updatedCategory.id ? updatedCategory : cat
      ));
    });

    socket.on('category_deleted', ({ id }) => {
      setCategories(prev => prev.filter(cat => cat.id !== id));
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (editingProduct) {
      const product = products.find(p => p.id === editingProduct);
      if (product) {
        setEditForm({
          title: product.title || '',
          price: product.price || '',
          image: product.image || '',
          features: product.features || [],
          type: product.type || '',
          description: product.description || '',
          deliveryType: product.deliveryType || 'serials',
          apiKeyType: product.apiKeyType || '',
          requiresGuildId: product.requiresGuildId || false,
          minQuantity: product.minQuantity || 1,
          maxQuantity: product.maxQuantity || 1000,
          memberType: product.memberType || 'online',
          discount: product.discount || 0,
          onHold: product.onHold || false,
          customUrl: product.customUrl || '',
          showBoostInstructions: product.showBoostInstructions || false
        });
      }
    }
  }, [editingProduct, products]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${editingProduct}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          title: editForm.title,
          price: editForm.price,
          image: editForm.image,
          features: editForm.features,
          type: editForm.type,
          description: editForm.description,
          deliveryType: editForm.deliveryType,
          apiKeyType: editForm.apiKeyType,
          requiresGuildId: editForm.requiresGuildId,
          minQuantity: editForm.minQuantity,
          maxQuantity: editForm.maxQuantity,
          memberType: editForm.memberType,
          discount: editForm.discount || 0,
          onHold: editForm.onHold,
          customUrl: editForm.customUrl || '',
          showBoostInstructions: editForm.showBoostInstructions
        })
      });

      if (response.ok) {
        const updatedProduct = await response.json();
        setProducts(prevProducts => 
          prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
        );
        setEditingProduct(null);
      }
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...editForm.features];
    newFeatures[index] = value;
    setEditForm({ ...editForm, features: newFeatures });
  };

  const addFeature = () => {
    setEditForm({
      ...editForm,
      features: [...editForm.features, '']
    });
  };

  const removeFeature = (index) => {
    const newFeatures = editForm.features.filter((_, i) => i !== index);
    setEditForm({ ...editForm, features: newFeatures });
  };

  const fetchProducts = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/products`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      }
    })
      .then(res => res.json())
      .then(data => {
        // Les produits sont déjà triés par position côté serveur
        setProducts(data);
        setLoading(false);
      })
      .catch(console.error);
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Supprimer le produit de l'état local
        setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
        
        // Fermer le formulaire d'édition si le produit supprimé était en cours d'édition
        if (editingProduct === productId) {
          setEditingProduct(null);
        }
        
        // Fermer la gestion du stock si le produit supprimé était sélectionné
        if (selectedProduct === productId) {
          setSelectedProduct(null);
        }
        
        alert('Product deleted successfully');
      } else {
        throw new Error(data.error || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product: ' + error.message);
    }
  };

  const addStock = async (productId) => {
    if (!stockInput.trim()) {
      return;
    }
    
    const items = stockInput.split('\n').filter(item => item.trim());
    if (items.length === 0) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${productId}/stock/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add stock');
      }

      const updatedProduct = await response.json();
      setStockInput('');
      setProducts(prevProducts => 
        prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
      );
    } catch (error) {
      console.error('Error adding stock:', error);
      alert('Failed to add stock: ' + error.message);
    }
  };

  const removeStockItem = async (productId, item) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${productId}/stock/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ items: [item] })
      });

      if (response.ok) {
        const updatedProduct = await response.json();
        setProducts(prevProducts => 
          prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
        );
      }
    } catch (error) {
      console.error('Error removing stock item:', error);
    }
  };

  const toggleProductHold = async (productId, currentHoldStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${productId}/toggle-hold`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ onHold: !currentHoldStatus })
      });

      if (response.ok) {
        const updatedProduct = await response.json();
        setProducts(prevProducts => 
          prevProducts.map(p => p.id === productId ? updatedProduct : p)
        );
      }
    } catch (error) {
      console.error('Error toggling product hold status:', error);
    }
  };

  const startReorderMode = () => {
    setReorderedProducts([...products]);
    setIsReorderMode(true);
  };

  const moveProductInList = (fromIndex, toIndex) => {
    const updatedProducts = [...reorderedProducts];
    const [movedProduct] = updatedProducts.splice(fromIndex, 1);
    updatedProducts.splice(toIndex, 0, movedProduct);
    setReorderedProducts(updatedProducts);
  };

  const saveNewOrder = async () => {
    try {
      // Verify API URL
      if (!process.env.REACT_APP_API_URL) {
        throw new Error('API URL is not configured');
      }

      // Verify auth token
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication token is missing');
      }

      // Prepare updated products array
      const updatedProducts = reorderedProducts.map((product, index) => ({
        id: product.id,
        position: index
      }));

      // Log request details
      console.log('=== Sending Reorder Request ===');
      console.log('API URL:', `${process.env.REACT_APP_API_URL}/api/admin/products/reorder-all`);
      console.log('Products to reorder:', updatedProducts.length);
      console.log('Request payload:', JSON.stringify({ products: updatedProducts }, null, 2));

      // Make the API request
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/reorder-all`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: updatedProducts })
      });

      // Log response details
      console.log('\n=== Response Details ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('Content-Type:', response.headers.get('content-type'));

      // Get response as text first
      const responseText = await response.text();
      console.log('\n=== Raw Response ===');
      console.log('Response text:', responseText);

      // Check if response is HTML (error page)
      if (responseText.trim().startsWith('<!DOCTYPE html>')) {
        throw new Error('Server returned an HTML error page instead of JSON. This might indicate a server-side error.');
      }

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('\n=== Parsed Response ===');
        console.log('Response data:', data);
      } catch (parseError) {
        console.error('\n=== JSON Parse Error ===');
        console.error('Parse error:', parseError);
        console.error('Response text:', responseText);
        throw new Error(`Failed to parse server response as JSON. Server returned: ${responseText.substring(0, 100)}...`);
      }

      // Check if response was successful
      if (!response.ok) {
        throw new Error(data.error || `Server responded with status ${response.status}`);
      }

      // Handle the response
      if (data.success) {
        // Update products state
        if (Array.isArray(data.products)) {
          setProducts(data.products);
        }
        setIsReorderMode(false);
        setReorderedProducts([]);
        toast.success(data.message || 'Product order saved successfully');
      } else {
        throw new Error(data.error || 'Failed to save product order');
      }
    } catch (error) {
      // Log error details
      console.error('\n=== Error in saveNewOrder ===');
      console.error('Error:', error);
      console.error('Stack:', error.stack);
      
      // Show error message
      toast.error(error.message || 'Failed to save product order');
      
      // Revert to original order
      setReorderedProducts([...products]);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Products</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => isReorderMode ? saveNewOrder() : startReorderMode()}
            className={`px-4 py-2 rounded ${
              isReorderMode
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-gray-500 hover:bg-gray-600'
            } text-white`}
          >
            {isReorderMode ? 'Save Order' : 'Edit Order'}
          </button>
          <button
            onClick={() => navigate('/admin/products/add?type=catalog')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Add Catalog Product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {(isReorderMode ? reorderedProducts : products).map((product, index) => (
          <div
            key={product.id}
            className={`bg-secondary p-6 rounded-lg ${
              isReorderMode ? 'cursor-move' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                {isReorderMode && (
                  <div className="flex items-center mr-4">
                    <input
                      type="number"
                      min="0"
                      max={products.length - 1}
                      value={index}
                      onChange={(e) => {
                        const newPosition = Math.min(
                          Math.max(0, parseInt(e.target.value) || 0),
                          products.length - 1
                        );
                        moveProductInList(index, newPosition);
                      }}
                      className="w-16 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-center"
                    />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-medium">{product.title}</h3>
                  <p className="text-gray-400">${product.price}</p>
                  {product.onHold && (
                    <span className="text-yellow-500 text-sm">On Hold</span>
                  )}
                  <span className={`ml-2 text-sm ${product.productType === 'catalog' ? 'text-blue-500' : 'text-green-500'}`}>
                    ({product.productType === 'catalog' ? 'Catalog' : 'Autobuy'})
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-400">
                  {product.productType === 'catalog' ? (
                    <span className="flex items-center">
                      24/24 <span className="ml-1 text-lg">∞</span>
                    </span>
                  ) : (
                    `Stock: ${product.deliveryType === 'explicit_api' ? 'Unlimited' : ((product.stock?.length || 0) + ' items')}`
                  )}
                </span>
                <button
                  onClick={() => setEditingProduct(product.id)}
                  className="text-blue-500 hover:text-blue-400"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteProduct(product.id)}
                  className="text-red-500 hover:text-red-400"
                >
                  Delete
                </button>
              </div>
            </div>

            {editingProduct === product.id && (
              <div className="mt-4 bg-gray-800 rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">Edit Product</h3>
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Image URL</label>
                    <input
                      type="text"
                      value={editForm.image}
                      onChange={(e) => setEditForm({ ...editForm, image: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                      placeholder="/images/your-image.gif"
                    />
                    <p className="text-sm text-gray-400 mt-1">
                      Place your images in the public/images/ folder and use the path like "/images/your-image.gif"
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Custom URL (optional)</label>
                    <input
                      type="text"
                      value={editForm.customUrl}
                      onChange={(e) => setEditForm({ ...editForm, customUrl: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                      placeholder="e.g. boost1"
                    />
                    <p className="text-sm text-gray-400 mt-1">This will create a custom URL like: localhost:3000/boost1</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Type</label>
                    <select
                      value={editForm.type}
                      onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                    >
                      <option value="">Select Type</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Features ou Description selon le type de produit */}
                  {product.productType === 'catalog' ? (
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-md p-2 h-32"
                        placeholder="Entrez une description détaillée du produit..."
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium mb-2">Features</label>
                      {editForm.features.map((feature, index) => (
                        <div key={index} className="flex space-x-2 mb-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureChange(index, e.target.value)}
                            className="flex-1 bg-gray-700 border border-gray-600 rounded-md p-2"
                          />
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-red-500 hover:text-red-400 px-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addFeature}
                        className="text-primary hover:text-primary-dark"
                      >
                        Add Feature
                      </button>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-300">Delivery Type</label>
                    <select
                      value={editForm.deliveryType || 'serials'}
                      onChange={(e) => setEditForm({ ...editForm, deliveryType: e.target.value })}
                      className="form-input w-full"
                      required
                    >
                      <option value="serials">Serials (One per line)</option>
                      <option value="explicit_api">Explicit API</option>
                    </select>
                  </div>

                  {editForm.deliveryType === 'explicit_api' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300">API Key Type</label>
                        <input
                          type="text"
                          value={editForm.apiKeyType || ''}
                          onChange={(e) => setEditForm({ ...editForm, apiKeyType: e.target.value })}
                          className="form-input w-full"
                          placeholder="members, boosts, etc."
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editForm.requiresGuildId || false}
                          onChange={(e) => setEditForm({ ...editForm, requiresGuildId: e.target.checked })}
                          className="form-checkbox"
                        />
                        <label className="text-sm font-medium text-gray-300">
                          Requires Discord Server ID
                        </label>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300">Min Quantity</label>
                          <input
                            type="number"
                            value={editForm.minQuantity || 1}
                            onChange={(e) => setEditForm({ ...editForm, minQuantity: parseInt(e.target.value) })}
                            className="form-input w-full"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300">Max Quantity</label>
                          <input
                            type="number"
                            value={editForm.maxQuantity || 1000}
                            onChange={(e) => setEditForm({ ...editForm, maxQuantity: parseInt(e.target.value) })}
                            className="form-input w-full"
                            min="1"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300">Member Type</label>
                        <select
                          value={editForm.memberType || 'online'}
                          onChange={(e) => setEditForm({ ...editForm, memberType: e.target.value })}
                          className="form-input w-full"
                          required
                        >
                          <option value="online">Online Members</option>
                          <option value="offline">Offline Members</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Discount (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={editForm.discount || 0}
                        onChange={(e) => setEditForm({ ...editForm, discount: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                        className="form-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Sales Status</label>
                      <div className="flex items-center space-x-2 mt-2">
                        <input
                          type="checkbox"
                          checked={editForm.onHold}
                          onChange={(e) => setEditForm({ ...editForm, onHold: e.target.checked })}
                          className="form-checkbox"
                        />
                        <span className="text-sm text-gray-300">Pause Sales</span>
                      </div>
                    </div>
                  </div>

                  {editForm.type === 'boost' && (
                    <div className="flex items-center space-x-2 mt-4">
                      <input
                        type="checkbox"
                        id="showBoostInstructions"
                        checked={editForm.showBoostInstructions}
                        onChange={(e) => setEditForm({ ...editForm, showBoostInstructions: e.target.checked })}
                        className="rounded border-gray-600 bg-[#141414] text-[#22c55e] focus:ring-[#22c55e]"
                      />
                      <label htmlFor="showBoostInstructions" className="text-sm text-gray-300">
                        Show boost instructions on delivery
                      </label>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setEditingProduct(null)}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            )}

            {selectedProduct === product.id && (
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Current Stock</h4>
                  <div className="bg-gray-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                    {!product.stock || product.stock.length === 0 ? (
                      <p className="text-gray-500">No items in stock</p>
                    ) : (
                      <ul className="space-y-2">
                        {product.stock.map((item, index) => (
                          <li key={index} className="flex justify-between items-center">
                            <span className="font-mono text-sm">{item}</span>
                            <button
                              onClick={() => removeStockItem(product.id, item)}
                              className="text-red-500 hover:text-red-400"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">Add Stock</h4>
                  <div className="space-y-2">
                    <textarea
                      value={stockInput}
                      onChange={(e) => setStockInput(e.target.value)}
                      placeholder="Enter items (one per line)"
                      className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-2 text-sm font-mono resize-none"
                    />
                    <button
                      onClick={() => addStock(product.id)}
                      disabled={!stockInput.trim()}
                      className={`w-full bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded transition-colors
                        ${!stockInput.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Add Items
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AddProduct = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const productType = new URLSearchParams(location.search).get('type') || 'autobuy';
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    image: '',
    features: [''],
    type: '',
    description: '',
    deliveryType: 'serials',
    apiKeyType: '',
    requiresGuildId: false,
    minQuantity: 1,
    maxQuantity: 1000,
    memberType: 'online',
    discount: 0,
    onHold: false,
    customUrl: '',
    showBoostInstructions: false,
    productType: productType // Ajouter le type de produit ici
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          ...formData,
          productType: productType // S'assurer que le type est envoyé
        })
      });

      if (response.ok) {
        navigate('/admin/products');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Add New Product</h2>
        <button
          onClick={() => navigate('/admin/products')}
          className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Price</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                placeholder="/images/your-image.gif"
              />
              <p className="text-sm text-gray-400 mt-1">
                Place your images in the public/images/ folder and use the path like "/images/your-image.gif"
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Custom URL (optional)</label>
              <input
                type="text"
                value={formData.customUrl}
                onChange={(e) => setFormData({ ...formData, customUrl: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                placeholder="e.g. boost1"
              />
              <p className="text-sm text-gray-400 mt-1">
                This will create a custom URL like: localhost:3000/boost1
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-md p-2"
                required
              >
                <option value="">Select Type</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Features ou Description selon le type de produit */}
            {productType === 'catalog' ? (
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-md p-2 h-32"
                  placeholder="Entrez une description détaillée du produit..."
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">Features</label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded-md p-2"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-red-500 hover:text-red-400 px-2"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFeature}
                  className="text-primary hover:text-primary-dark"
                >
                  Add Feature
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300">Delivery Type</label>
              <select
                value={formData.deliveryType}
                onChange={(e) => setFormData({ ...formData, deliveryType: e.target.value })}
                className="form-input w-full"
                required
              >
                <option value="serials">Serials (One per line)</option>
                <option value="explicit_api">Explicit API</option>
              </select>
            </div>

            {formData.deliveryType === 'explicit_api' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300">API Key Type</label>
                  <input
                    type="text"
                    value={formData.apiKeyType}
                    onChange={(e) => setFormData({ ...formData, apiKeyType: e.target.value })}
                    className="form-input w-full"
                    placeholder="members, boosts, etc."
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.requiresGuildId}
                    onChange={(e) => setFormData({ ...formData, requiresGuildId: e.target.checked })}
                    className="form-checkbox"
                  />
                  <label className="text-sm font-medium text-gray-300">
                    Requires Discord Server ID
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Min Quantity</label>
                    <input
                      type="number"
                      value={formData.minQuantity}
                      onChange={(e) => setFormData({ ...formData, minQuantity: parseInt(e.target.value) })}
                      className="form-input w-full"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Max Quantity</label>
                    <input
                      type="number"
                      value={formData.maxQuantity}
                      onChange={(e) => setFormData({ ...formData, maxQuantity: parseInt(e.target.value) })}
                      className="form-input w-full"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300">Member Type</label>
                  <select
                    value={formData.memberType}
                    onChange={(e) => setFormData({ ...formData, memberType: e.target.value })}
                    className="form-input w-full"
                    required
                  >
                    <option value="online">Online Members</option>
                    <option value="offline">Offline Members</option>
                  </select>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300">Discount (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300">Sales Status</label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    checked={formData.onHold}
                    onChange={(e) => setFormData({ ...formData, onHold: e.target.checked })}
                    className="form-checkbox"
                  />
                  <span className="text-sm text-gray-300">Pause Sales</span>
                </div>
              </div>
            </div>

            {formData.type === 'boost' && (
              <div className="flex items-center space-x-2 mt-4">
                <input
                  type="checkbox"
                  id="showBoostInstructions"
                  checked={formData.showBoostInstructions}
                  onChange={(e) => setFormData({ ...formData, showBoostInstructions: e.target.checked })}
                  className="rounded border-gray-600 bg-[#141414] text-[#22c55e] focus:ring-[#22c55e]"
                />
                <label htmlFor="showBoostInstructions" className="text-sm text-gray-300">
                  Show boost instructions on delivery
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded"
          >
            {loading ? 'Creating...' : 'Create Product'}
          </button>
        </div>
      </form>
    </div>
  );
};

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ id: '', name: '', icon: '' });
  const [selectedIcon, setSelectedIcon] = useState('');

  const iconList = [
    { name: 'ServerBoostIcon', label: 'Server Boost' },
    { name: 'UsersIcon', label: 'Users' },
    { name: 'SparklesIcon', label: 'Sparkles' },
    { name: 'ToolsIcon', label: 'Tools' },
    { name: 'HashtagIcon', label: 'Hashtag' },
    { name: 'StarIcon', label: 'Star' },
    { name: 'RocketIcon', label: 'Rocket' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.id || !newCategory.name || !selectedIcon) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          ...newCategory,
          icon: selectedIcon
        })
      });

      if (response.ok) {
        const addedCategory = await response.json();
        setCategories(prev => [...prev, addedCategory]);
        setNewCategory({ id: '', name: '', icon: '' });
        setSelectedIcon('');
      }
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(editingCategory)
      });

      if (response.ok) {
        setCategories(prevCategories =>
          prevCategories.map(cat => cat.id === editingCategory.id ? editingCategory : cat)
        );
        setEditingCategory(null);
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This will affect all products in this category.')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.ok) {
        setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Categories</h2>
      </div>

      {/* Add New Category Form */}
      <div className="bg-secondary p-6 rounded-lg mb-6">
        <h3 className="text-lg font-medium mb-4">Add New Category</h3>
        <form onSubmit={handleAddCategory} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Category ID (e.g., boost)"
              value={newCategory.id}
              onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-md p-2"
            />
            <input
              type="text"
              placeholder="Category Name (e.g., Server Boosts)"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Select Icon</label>
            <div className="grid grid-cols-7 gap-4">
              {iconList.map((icon) => {
                const IconComponent = IconComponents[icon.name];
                return (
                  <button
                    key={icon.name}
                    type="button"
                    onClick={() => setSelectedIcon(icon.name)}
                    className={`p-4 rounded-lg flex flex-col items-center justify-center transition-colors ${
                      selectedIcon === icon.name
                        ? 'bg-[#22c55e] text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <div className="w-6 h-6 mb-2 text-gray-400">
                      <IconComponent />
                    </div>
                    <span className="text-xs">{icon.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={!newCategory.id || !newCategory.name || !selectedIcon}
            className={`bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded w-full ${
              (!newCategory.id || !newCategory.name || !selectedIcon) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Add Category
          </button>
        </form>
      </div>

      {/* Categories List */}
      <div className="grid grid-cols-1 gap-4">
        {categories.map(category => (
          <div key={category.id} className="bg-secondary p-6 rounded-lg">
            {editingCategory?.id === category.id ? (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-md p-2"
                  />
                  <div>
                    <label className="block text-sm font-medium mb-2">Select Icon</label>
                    <div className="grid grid-cols-7 gap-4">
                      {iconList.map((icon) => {
                        const IconComponent = IconComponents[icon.name];
                        return (
                          <button
                            key={icon.name}
                            type="button"
                            onClick={() => setEditingCategory({ ...editingCategory, icon: icon.name })}
                            className={`p-4 rounded-lg flex flex-col items-center justify-center transition-colors ${
                              editingCategory.icon === icon.name
                                ? 'bg-[#22c55e] text-white'
                                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                            }`}
                          >
                            <div className="w-6 h-6 mb-2 text-gray-400">
                              <IconComponent />
                            </div>
                            <span className="text-xs">{icon.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setEditingCategory(null)}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-2xl w-8 h-8 text-gray-400">
                    {IconComponents[category.icon] && React.createElement(IconComponents[category.icon])}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium">{category.name}</h3>
                    <p className="text-sm text-gray-400">ID: {category.id}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingCategory(category)}
                    className="text-blue-500 hover:text-blue-400"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-500 hover:text-red-400"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [activePage, setActivePage] = useState('terms');

  useEffect(() => {
    // Update currentSection based on URL path
    const path = location.pathname.split('/').pop();
    if (path && path !== 'admin') {
      setCurrentSection(path);
    } else {
      setCurrentSection('dashboard');
    }
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (section) => {
    return currentSection === section;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-[#0a0a0a] min-h-screen glass-card">
          <nav className="mt-8">
            <div className="px-4">
              <Link
                to="/admin"
                className={`flex items-center px-4 py-3 mb-2 rounded-lg transition-colors ${
                  isActive('dashboard') ? 'bg-[#22c55e] text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span>Dashboard</span>
              </Link>
              <Link
                to="/admin/products"
                className={`flex items-center px-4 py-3 mb-2 rounded-lg transition-colors ${
                  isActive('products') ? 'bg-[#22c55e] text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span>Products</span>
              </Link>
              <Link
                to="/admin/categories"
                className={`flex items-center px-4 py-3 mb-2 rounded-lg transition-colors ${
                  isActive('categories') ? 'bg-[#22c55e] text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span>Categories</span>
              </Link>
              <Link
                to="/admin/pages"
                className={`flex items-center px-4 py-3 mb-2 rounded-lg transition-colors ${
                  isActive('pages') ? 'bg-[#22c55e] text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span>Pages</span>
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3 mb-2 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
              >
                <span>Logout</span>
              </button>
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<ProductList />} />
              <Route path="products/add" element={<AddProduct />} />
              <Route path="categories" element={<CategoryList />} />
              <Route path="pages" element={
                <div className="space-y-6">
                  <div className="flex space-x-4 mb-8">
                    <button
                      onClick={() => setActivePage('terms')}
                      className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                        activePage === 'terms'
                          ? 'bg-[#22c55e] text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Terms of Service
                    </button>
                    <button
                      onClick={() => setActivePage('privacy')}
                      className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                        activePage === 'privacy'
                          ? 'bg-[#22c55e] text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Privacy Policy
                    </button>
                    <button
                      onClick={() => setActivePage('faq')}
                      className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                        activePage === 'faq'
                          ? 'bg-[#22c55e] text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      FAQ
                    </button>
                  </div>
                  <PageEditor pageType={activePage} />
                </div>
              } />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 